import { SelectionModel } from '@angular/cdk/collections';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { CanPipe, DateNotExpiredPipe, NestedTreeNode, anyAsset } from '@thehubz/client/core';
import { LicenseRegion } from '@thehubz/shared/model';
@Component({
  selector: 'sp-mat-tree',
  templateUrl: './mat-tree.component.html',
  styleUrls: ['./mat-tree.component.scss'],
})
export class MatTreeComponent implements OnChanges {
  /**
   * Mat Tree data source
   */
  private _matTreeDataSource!: NestedTreeNode[];
  @Input() set matTreeDataSource(matTreeDataSource: NestedTreeNode[] | NestedTreeNode) {
    if (matTreeDataSource) this._matTreeDataSource = Array.isArray(matTreeDataSource) ? matTreeDataSource : [matTreeDataSource];
  }

  get matTreeDataSource(): NestedTreeNode[] {
    return this._matTreeDataSource;
  }
  // @Input() matTreeDataSource!: NestedTreeNode[];
  /**
   * Check all checkbox, boolean value
   */
  @Input() selectAllCheckboxDefault = false;
  /**
   * Set border color for Required (boolean)
   */
  @Input() required = false;
  /**
   * Show selectAll topmost parent checkbox (boolean)
   */
  @Input() showSelectAll = true;
  /**
   * Show expand and collapse buttons (boolean)
   */
  @Input() showExpandCollapse = true;
  /**
   * Show outer border (boolean)
   */
  @Input() showBorder = true;
  /**
   * Show expiry date (boolean)
   */
  @Input() showExpiryDate = false;
  /**
   * Emit value when checkbox checked or unchecked
   */

  @Output() isListChecked = new EventEmitter<boolean>();
  /**
   * Emit selected node list
   */
  @Output() nodeList = new EventEmitter<NestedTreeNode[]>();

  AnyAsset = anyAsset;

  licenseRegionLabels = LicenseRegion;

  allComplete = false;
  /** The selection for checklist */
  checklistSelection = new SelectionModel<NestedTreeNode>(true /* multiple */);
  // The TreeControl controls the expand/collapse state of tree nodes.
  treeControl = new NestedTreeControl<NestedTreeNode>((node) => node?.children);
  // mat tree data source
  dataSource = new MatTreeNestedDataSource<NestedTreeNode>();
  // used with mat-nested-tree-node to check if node children exist
  hasChild = (_: number, node: NestedTreeNode) => !!node.children && node.children.length > 0;

  constructor(private dateNotExpiredPipe: DateNotExpiredPipe, private canPipe: CanPipe) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['matTreeDataSource'] && changes['matTreeDataSource'].currentValue) {
      this.dataSource.data = this.matTreeDataSource;
      this.treeControl.dataNodes = this.matTreeDataSource;
      // expand 1st child node of every parent node
      this.matTreeDataSource.forEach((data) => {
        this.treeControl.expand(data);
      });
      // set the parent for each node
      Object.keys(this.dataSource.data).forEach((key) => this.setParent(this.dataSource.data[key as unknown as number], undefined));
      // all checkbox will be checked to true on component load
      if (this.selectAllCheckboxDefault) {
        this.treeControl.dataNodes.forEach((node, i) => this.itemToggle(true, this.treeControl.dataNodes[i]));
      }
      this.isListChecked.emit(this.matTreeDataSource.every((dataSource) => dataSource.indeterminate));
    }
  }

  someComplete(): boolean {
    if (this.treeControl.dataNodes === null) {
      return false;
    }
    return this.treeControl.dataNodes.filter((t) => t.selected || t.indeterminate).length > 0 && !this.allComplete;
  }

  setAll(completed: boolean) {
    this.allComplete = completed;
    if (this.treeControl.dataNodes === null) {
      return;
    }
    this.isListChecked.emit(this.allComplete);
    this.changeTreeControlNode(this.treeControl.dataNodes, completed);
    this.nodeList.emit(this.dataSource.data);
  }

  changeTreeControlNode(dataNodes: NestedTreeNode[], completed: boolean) {
    for (const node of dataNodes) {
      const expiryDate = node?.['expiryDate'];
      if (node.children) {
        if (!this.dateNotExpiredPipe.transform(expiryDate) && !this.canPipe.transform(this.AnyAsset, 'expired.download')) {
          node.indeterminate = false;
          node.selected = false;
          this.changeTreeControlNode(node.children, false);
        } else {
          node.indeterminate = completed;
          node.selected = completed;
          this.changeTreeControlNode(node.children, completed);
        }
      } else {
        node.selected = completed;
      }
    }
  }

  itemToggle(checked: boolean, node: NestedTreeNode) {
    // set required validation on clients and brands
    node.selected = checked;
    if (node.children) {
      node.children.forEach((child) => {
        this.itemToggle(checked, child);
      });
    }
    this.checkAllParents(node, this.treeControl);
    this.nodeList.emit(this.dataSource.data);
    // flatting the the tree structure to show all leaf node in one array
    const flatLeafNode = this.matTreeDataSource.flatMap((elem) => elem.children?.flatMap((elem) => elem.children));
    const checkSelected = flatLeafNode.every((dataSource) => dataSource?.selected);
    this.allComplete = checkSelected;

    this.isListChecked.emit(this.matTreeDataSource.every((dataSource) => dataSource.indeterminate));
  }
  // select parents of the selected descendants
  checkAllParents(node: NestedTreeNode, treeControl: NestedTreeControl<NestedTreeNode>) {
    if (node.parent) {
      const descendants = treeControl.getDescendants(node.parent);
      node.parent.selected = descendants.every((child) => child.selected);
      node.parent.indeterminate = descendants.some((child) => child.selected);
      this.checkAllParents(node.parent, treeControl);
    }
  }
  // set parent for tree object
  setParent(node: NestedTreeNode, parent: NestedTreeNode | undefined) {
    node.parent = parent;
    if (node.children) {
      node.children.forEach((childNode: NestedTreeNode) => {
        this.setParent(childNode, node);
      });
    }
  }
  // expand mat tree
  expandTree() {
    this.treeControl.dataNodes = this.matTreeDataSource;
    this.treeControl.expandAll();
  }
  // collapse mat tree
  collapseTree() {
    this.matTreeDataSource.forEach((data) => {
      data.children?.forEach((child) => this.treeControl.collapse(child));
    });
  }
}
